import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { ClubPaymentSettingsFull, UserClubSubscription } from "@/api/clubsApi";
import { ClubPricingSelector } from "./ClubPricingSelector";

interface ClubPaymentButtonProps {
  clubId: string;
  userId: string;
  paymentSettings: ClubPaymentSettingsFull | null;
  userSubscription: UserClubSubscription | null | undefined;
}

export function ClubPaymentButton({
  clubId,
  userId,
  paymentSettings,
  userSubscription,
}: ClubPaymentButtonProps) {
  const [loading, setLoading] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState<"month" | "year">("year");

  if (!paymentSettings || !userId) {
    return null;
  }

  const hasMembership =
    userSubscription?.type === "membership" &&
    ["active", "trialing"].includes(userSubscription.status || "");

  const canPayMembership = paymentSettings.membership_enabled && !hasMembership;

  if (!canPayMembership) {
    return null;
  }

  const hasMonthlyPrice =
    paymentSettings.membership_price_monthly && paymentSettings.membership_price_monthly > 0;
  const hasYearlyPrice =
    paymentSettings.membership_price_yearly && paymentSettings.membership_price_yearly > 0;

  if (!hasMonthlyPrice && !hasYearlyPrice) {
    return null;
  }

  const handlePayment = async () => {
    setLoading(true);

    try {
      let price = 0;
      if (selectedInterval === "month") price = paymentSettings.membership_price_monthly || 0;
      else if (selectedInterval === "year") price = paymentSettings.membership_price_yearly || 0;

      if (price === 0) {
        setLoading(false);
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      const origin = window.location.origin;
      const successUrl = `${origin}/clubs/${clubId}?payment=success`;
      const cancelUrl = `${origin}/clubs/${clubId}?payment=cancelled`;

      const response = await supabase.functions.invoke("create-club-checkout", {
        body: {
          clubId,
          userId,
          paymentType: "membership",
          interval: selectedInterval,
          successUrl,
          cancelUrl,
          platform: "web",
        },
      });

      if (response.error) {
        throw response.error;
      }

      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (error) {
      console.error("Payment error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="text-body-2 text-text-secondary mb-4">
        {paymentSettings.membership_description || "Get full access to the club"}
      </p>

      <ClubPricingSelector
        paymentSettings={paymentSettings}
        selectedInterval={selectedInterval}
        onSelectInterval={setSelectedInterval}
        onContinue={handlePayment}
        loading={loading}
        trialDays={paymentSettings.membership_trial_days || 0}
      />
    </div>
  );
}
